import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, PanelLeftOpen } from "lucide-react";
import { useSidebar } from "@/lib/sidebar";
import { ApiError, authApi, type UserOut } from "@/lib/api";
import { useSession } from "@/lib/useAuth";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Field, Input } from "@/components/ui/Field";
import { Checkbox, Select } from "@/components/ui/Toggle";
import { Alert } from "@/components/ui/Alert";
import { formatTs } from "@/lib/format";
import { cn } from "@/lib/cn";

const ROLE_OPTIONS = [
  { value: "user", label: "用户" },
  { value: "admin", label: "管理员" },
];

function UserRow({
  user,
  isSelf,
  onToggleDisabled,
  pending,
}: {
  user: UserOut;
  isSelf: boolean;
  onToggleDisabled: (u: UserOut) => void;
  pending: boolean;
}): JSX.Element {
  return (
    <tr className="hover:bg-zinc-50">
      <td className="px-4 py-2 text-sm font-medium text-zinc-900">
        {user.username}
        {isSelf ? <span className="ml-2 text-xs text-zinc-400">(本人)</span> : null}
      </td>
      <td className="px-4 py-2">
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-xs font-medium",
            user.role === "admin" ? "bg-violet-100 text-violet-700" : "bg-zinc-100 text-zinc-600",
          )}
        >
          {user.role === "admin" ? "管理员" : "用户"}
        </span>
      </td>
      <td className="px-4 py-2 text-xs text-zinc-500">{formatTs(user.created_at)}</td>
      <td className="px-4 py-2 font-mono text-xs text-zinc-400">{user.user_id.slice(0, 12)}…</td>
      <td className="px-4 py-2 text-right">
        {isSelf ? (
          <span className="text-xs text-zinc-400">不可禁用本人</span>
        ) : (
          <Button
            size="sm"
            variant={user.disabled ? "secondary" : "danger"}
            onClick={() => onToggleDisabled(user)}
            disabled={pending}
          >
            {user.disabled ? "启用" : "禁用"}
          </Button>
        )}
      </td>
    </tr>
  );
}

export function AdminUsersPage(): JSX.Element {
  const session = useSession();
  const queryClient = useQueryClient();
  const sidebar = useSidebar();
  const [includeDisabled, setIncludeDisabled] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<"admin" | "user">("user");
  const [createError, setCreateError] = useState<string | null>(null);
  const [createInfo, setCreateInfo] = useState<string | null>(null);

  const queryKey = ["users", "list", includeDisabled] as const;
  const query = useQuery({
    queryKey,
    queryFn: () => authApi.listUsers(session.token, includeDisabled),
  });

  const createMutation = useMutation({
    mutationFn: () => authApi.createUser(session.token, { username: username.trim(), password, role }),
    onSuccess: (u) => {
      setCreateError(null);
      setCreateInfo(`已创建 ${u.username} (${u.role})`);
      setUsername("");
      setPassword("");
      setRole("user");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (err: unknown) => {
      setCreateInfo(null);
      setCreateError(err instanceof ApiError ? err.message : "创建失败");
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (u: UserOut) =>
      authApi.patchUser(session.token, u.user_id, { disabled: !u.disabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const onCreateSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    createMutation.mutate();
  };

  const users = query.data?.users ?? [];
  const enabled = users.filter((u) => !u.disabled);
  const disabled = users.filter((u) => u.disabled);

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-5xl space-y-5 px-6 py-6">
        <header>
          <div className="flex items-center gap-2 text-[13px] text-zinc-500">
            {sidebar.collapsed ? (
              <button
                type="button"
                onClick={sidebar.toggle}
                className="-ml-2 rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
                title="展开侧栏"
              >
                <PanelLeftOpen className="h-4 w-4" />
              </button>
            ) : null}
            <span>Liando</span>
            <span className="text-zinc-300">/</span>
            <span className="text-zinc-800">用户管理</span>
          </div>
          <p className="mt-1 text-[12px] text-zinc-400">
            仅管理员可见; 禁用账号会立即吊销其所有 token。
          </p>
        </header>

      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <CardHeader
            title="账号列表"
            action={
              <Checkbox
                checked={includeDisabled}
                onChange={(e) => setIncludeDisabled(e.target.checked)}
                label="包含已禁用"
              />
            }
          />
          <CardBody className="p-0">
            {query.isLoading ? (
              <p className="px-6 py-4 text-sm text-zinc-400">加载中…</p>
            ) : query.isError ? (
              <p className="px-6 py-4 text-sm text-red-600">用户列表加载失败。</p>
            ) : users.length === 0 ? (
              <p className="px-6 py-4 text-sm text-zinc-400">暂无用户。</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">用户名</th>
                      <th className="px-4 py-2 font-medium">角色</th>
                      <th className="px-4 py-2 font-medium">创建时间</th>
                      <th className="px-4 py-2 font-medium">user_id</th>
                      <th className="px-4 py-2 font-medium text-right">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {enabled.map((u) => (
                      <UserRow
                        key={u.user_id}
                        user={u}
                        isSelf={u.user_id === session.user_id}
                        onToggleDisabled={(uu) => toggleMutation.mutate(uu)}
                        pending={toggleMutation.isPending}
                      />
                    ))}
                    {disabled.length > 0 ? (
                      <tr className="bg-zinc-50">
                        <td colSpan={5} className="px-4 py-1.5 text-xs font-medium text-zinc-500">
                          已禁用 ({disabled.length})
                        </td>
                      </tr>
                    ) : null}
                    {disabled.map((u) => (
                      <UserRow
                        key={u.user_id}
                        user={u}
                        isSelf={u.user_id === session.user_id}
                        onToggleDisabled={(uu) => toggleMutation.mutate(uu)}
                        pending={toggleMutation.isPending}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                新建账号
              </span>
            }
            description="新用户立即可登录; 角色后续不可改, 误开 admin 请直接禁用。"
          />
          <CardBody>
            <form onSubmit={onCreateSubmit} className="space-y-4">
              {createError ? <Alert tone="error">{createError}</Alert> : null}
              {createInfo ? <Alert tone="success">{createInfo}</Alert> : null}
              <Field id="new-username" label="用户名" required hint="3 – 32 字符">
                <Input
                  id="new-username"
                  required
                  minLength={3}
                  maxLength={32}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={createMutation.isPending}
                />
              </Field>
              <Field id="new-password" label="初始密码" required hint="至少 8 位">
                <Input
                  id="new-password"
                  type="password"
                  required
                  minLength={8}
                  maxLength={128}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={createMutation.isPending}
                />
              </Field>
              <Field id="new-role" label="角色" required>
                <Select
                  id="new-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as "admin" | "user")}
                  options={ROLE_OPTIONS}
                  disabled={createMutation.isPending}
                />
              </Field>
              <Button
                type="submit"
                className="w-full"
                disabled={createMutation.isPending || !username.trim() || password.length < 8}
              >
                {createMutation.isPending ? "创建中…" : "创建账号"}
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>
      </div>
    </div>
  );
}
